import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save } from "lucide-react";

export default function AdminFastrrCheckout() {
  const [active, setActive] = useState(false);
  const [on, setOn] = useState(false);
  const [gokwikOn, setGokwikOn] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [webhook, setWebhook] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/merchant/config/fastrr-checkout").then(({ data }) => {
      setActive(Boolean(data.active));
      setOn(Boolean(data.on));
      setGokwikOn(Boolean(data.gokwik_on));
      setCfg(data.fastrr_checkout || { api_key: "", sandbox: false, has_api_secret: false });
      setWebhook(data.webhook_url || "");
    }).catch((e) => toast.error(extractErr(e)));
  }, []);

  if (!cfg) return <div className="text-slate-500">Loading…</div>;
  if (!active) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
        <Card className="p-8 text-center text-slate-600">Unlock <strong>Shiprocket Fastrr Checkout</strong> from the App Store to replace native checkout with Fastrr.</Card>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const payload = { api_key: cfg.api_key, sandbox: Boolean(cfg.sandbox) };
      if (cfg.api_secret) payload.api_secret = cfg.api_secret;
      const { data } = await api.put("/merchant/config/fastrr-checkout", payload);
      setCfg({ ...data.fastrr_checkout, api_secret: "" });
      setWebhook(data.webhook_url || webhook);
      toast.success("Fastrr Checkout saved");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="fr-back"><ArrowLeft className="w-4 h-4" /> App Store</Link>
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Shiprocket Fastrr Checkout</h1>
        <p className="text-sm text-slate-500 mt-1">When this addon is on and keys are saved, cart and checkout open Fastrr instead of the native payment form. INR only. Only one of Fastrr or Gokwik Checkout can be on.</p>
      </div>
      {gokwikOn && !on && (
        <Card className="p-4 text-sm text-amber-800 bg-amber-50 border-amber-200" data-testid="fr-gokwik-note">
          Gokwik Checkout is currently on. Switch this addon on from App Store to use Fastrr — Gokwik will turn off automatically.
        </Card>
      )}
      <Card className="p-6 space-y-5 border-slate-200/70">
        <div>
          <Label htmlFor="fr-api-key">API Key</Label>
          <Input id="fr-api-key" className="mt-1 font-mono" data-testid="fr-api-key" value={cfg.api_key || ""} onChange={(e) => setCfg({ ...cfg, api_key: e.target.value })} placeholder="From Fastrr → Settings → Custom-Built" />
        </div>
        <div>
          <Label htmlFor="fr-api-secret">API Secret</Label>
          <Input id="fr-api-secret" type="password" className="mt-1 font-mono" data-testid="fr-api-secret" value={cfg.api_secret || ""} onChange={(e) => setCfg({ ...cfg, api_secret: e.target.value })} placeholder={cfg.has_api_secret ? "Saved — leave blank to keep" : "Paste API Secret"} />
          <p className="text-xs text-slate-500 mt-1">{cfg.has_api_secret ? "A secret is already stored. Leave blank to keep it." : "Required to create Fastrr checkout tokens."}</p>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor="fr-sandbox">Sandbox mode</Label>
            <p className="text-xs text-slate-500 mt-0.5">Use Fastrr sandbox API and UI. Turn off for live orders.</p>
          </div>
          <Switch id="fr-sandbox" checked={Boolean(cfg.sandbox)} onCheckedChange={(sandbox) => setCfg({ ...cfg, sandbox })} data-testid="fr-sandbox" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 space-y-1" data-testid="fr-webhook">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Order webhook (paste in Fastrr custom endpoints)</div>
          <code className="text-xs break-all text-slate-800">{webhook || "/api/store/{slug}/fastrr/webhook"}</code>
          <p className="text-xs text-slate-500">Auth: header <span className="font-mono">X-Api-Key</span> (and optional <span className="font-mono">X-Api-HMAC-SHA256</span> of the body).</p>
        </div>
        <Button type="button" onClick={save} disabled={saving} data-testid="fr-save">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}
        </Button>
      </Card>
    </div>
  );
}
